import { glob, styled } from "goober";
import {
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { playClick } from "../../../lib/sounds";
import { navigate, routes, useCurrentUrl } from "../../../lib/urls";
import { Icon } from "../../Icon";
import { SortableHeaderContent, SortArrow } from "../../ListTable";
import { Text } from "../../Text";
import { BottomToolbar } from "./BottomToolbar";
import type {
  AccessModel,
  EdgeType,
  GraphNode,
  NodeKind,
} from "./buildAccessModel";
import { canDrop } from "./dragGrant";
import { GraphEdges, HoverEdges } from "./GraphEdges";
import { type ChipState, GraphNodeChip } from "./GraphNodeChip";
import { type MenuAction, NodeContextMenu } from "./NodeContextMenu";
import {
  type ColumnSort,
  layoutGraph,
  NODE_HEIGHT,
  type NodePosition,
  NODE_WIDTH,
  ROW_PITCH,
} from "./layoutGraph";

const HEADER_HEIGHT = 48;
const DRAG_THRESHOLD = 5;
// Dragging within this many px of a scroll edge pans the graph so off-screen
// cards become reachable as drop targets.
const EDGE_SCROLL_ZONE = 64;
const EDGE_SCROLL_MAX = 20;

// Grabbing cursor everywhere while a card is being dragged (the ghost itself is
// pointer-events:none so the cursor would otherwise fall through to the cards).
glob`
  body[data-ag-dragging="true"] * {
    cursor: grabbing !important;
  }
  body[data-ag-dragging="true"] [data-more] {
    display: none;
  }
`;

const Viewport = styled("div")`
  flex: 1;
  min-height: 0;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const Scroll = styled("div")`
  flex: 1;
  overflow: auto;
  overscroll-behavior: none;
  position: relative;
`;

const HeaderBar = styled("div")`
  position: sticky;
  top: 0;
  left: 0;
  z-index: 2;
  height: ${HEADER_HEIGHT}px;
  /* Span the full viewport even when the graph is narrower, so the divider
     reaches the right edge; the inline width still covers the scrolled case. */
  min-width: 100%;
  background: var(--surface-secondary);
  box-shadow: 0 0.5px 0 var(--border-alpha);
`;

const ColumnSlot = styled("div")`
  position: absolute;
  top: 0;
  height: ${HEADER_HEIGHT}px;
  display: flex;
  align-items: center;
  padding-left: 8px;
`;

const Canvas = styled("div")`
  position: relative;
  z-index: 0;
  margin: 24px;
`;

// A faithful copy of the card, lifted off the surface, that tracks the cursor
// at the point it was grabbed.
const Ghost = styled("div")`
  position: fixed;
  z-index: 9999;
  pointer-events: none;
  display: flex;
  align-items: center;
  gap: 10px;
  width: ${NODE_WIDTH}px;
  height: ${NODE_HEIGHT}px;
  padding: 8px;
  box-sizing: border-box;
  background: var(--surface-primary);
  border-radius: 12px;
  box-shadow:
    0 0 0 0.5px var(--alpha4),
    var(--elevation-high);
  transform: scale(1.03) rotate(-3deg);
`;

const GhostBody = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  flex: 1;
`;

type DragState = {
  source: GraphNode;
  x: number;
  y: number;
  dx: number;
  dy: number;
  overId: string | null;
};

// "Search by anything": every node by name, plus members by email (their
// subtitle). Other kinds' subtitles are member counts, so we don't match those.
function nodeMatches(node: GraphNode, q: string): boolean {
  return (
    node.name.toLowerCase().includes(q) ||
    (node.kind === "user" &&
      !!node.subtitle &&
      node.subtitle.toLowerCase().includes(q))
  );
}

function toggleFilter(
  key: "collapse" | "hideDirect" | "hideInherited" | "hideAvailable",
  current: string | undefined
) {
  navigate(
    routes.monitoring.access,
    { [key]: current ? "" : "true" },
    { replace: true }
  );
}

type Props = {
  model: AccessModel;
  visualById: ReadonlyMap<string, ReactNode>;
  footerVisual: (node: GraphNode) => ReactNode;
  query: string;
  focusId?: string;
  onFocus: (id: string | null) => void;
  onSelect: (node: GraphNode) => void;
  onDrop: (source: GraphNode, target: GraphNode) => void;
  onRemove: (active: GraphNode, target: GraphNode) => void;
  nodeActions: (node: GraphNode) => MenuAction[];
  membershipLocked: boolean;
  membershipReason: string | null;
  canGrant: (source: GraphNode, target: GraphNode) => boolean;
};

// Dragging a user onto a group edits that group's membership, so it's blocked
// whenever the IdP owns group membership; every other drop (granting access to a
// scope) stays available.
function isMembershipDrop(source: GraphNode, target: GraphNode): boolean {
  return source.kind === "user" && target.kind === "group";
}

// Click a node to highlight its access path; drag a node onto a valid target to
// grant access; a card's hover more-menu views, revokes a connection, or
// deletes it; Space opens the highlighted node.
export function AccessGraphView({
  model,
  visualById,
  footerVisual,
  query,
  focusId,
  onFocus,
  onSelect,
  onDrop,
  onRemove,
  nodeActions,
  membershipLocked,
  membershipReason,
  canGrant,
}: Props) {
  const selectedId = focusId && model.nodeById.has(focusId) ? focusId : null;
  const { collapse, hideDirect, hideInherited, hideAvailable } = useCurrentUrl(
    routes.monitoring.access
  );
  const hideUnrelated = Boolean(collapse);
  const showDirect = !hideDirect;
  const showInherited = !hideInherited;
  const showAvailable = !hideAvailable;
  const onToggleHide = useCallback(
    () => toggleFilter("collapse", collapse),
    [collapse]
  );
  const onToggleInherited = useCallback(
    () => toggleFilter("hideInherited", hideInherited),
    [hideInherited]
  );
  const [columnSort, setColumnSort] = useState<ColumnSort>({});
  const [drag, setDrag] = useState<DragState | null>(null);
  const [menu, setMenu] = useState<{ targetId: string } | null>(null);
  const [hoverLayer, setHoverLayer] = useState<EdgeType | null>(null);
  const [collapseAmount, setCollapseAmount] = useState(0);
  const collapseAmountRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  const layout = useMemo(
    () => layoutGraph(model.nodes, model.edges, columnSort),
    [model, columnSort]
  );

  const columnNodeIds = useMemo(() => {
    const cols: string[][] = layout.columns.map(() => []);
    for (const [id, pos] of layout.positions) cols[pos.col]![pos.row] = id;
    return cols;
  }, [layout]);

  const connectedIds = useMemo(() => {
    if (!selectedId) return null;
    const set = new Set<string>([selectedId]);
    for (const e of model.edges) {
      if (e.sourceId === selectedId) set.add(e.targetId);
      else if (e.targetId === selectedId) set.add(e.sourceId);
    }
    return set;
  }, [selectedId, model.edges]);

  const matchedIds = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return new Set(
      model.nodes.filter((n) => nodeMatches(n, q)).map((n) => n.id)
    );
  }, [query, model]);

  // The edge layers (direct/inherited/derived) that touch each node, so a card
  // whose every edge is in a hidden layer can blur alongside its hidden lines.
  const nodeLayers = useMemo(() => {
    const m = new Map<string, Set<"direct" | "inherited" | "derived">>();
    const add = (id: string, c: "direct" | "inherited" | "derived") => {
      const set = m.get(id) ?? new Set<"direct" | "inherited" | "derived">();
      set.add(c);
      m.set(id, set);
    };
    for (const e of model.edges) {
      const c =
        e.type === "inherited"
          ? "inherited"
          : e.type === "derived"
            ? "derived"
            : "direct";
      add(e.sourceId, c);
      add(e.targetId, c);
    }
    return m;
  }, [model.edges]);

  // Hiding a card leaves a gap; in hide mode the survivors glide up to close it.
  // `visibleSet` is the focused path (or search matches); each survivor shifts
  // up past the hidden cards above it in its column, scaled by `collapseAmount`
  // (eased 0→1) so the cards and the edges between them animate together.
  const visibleSet = selectedId ? connectedIds : matchedIds;
  const collapsing = hideUnrelated && visibleSet != null;

  const renderPositions = useMemo(() => {
    if (collapseAmount === 0) return layout.positions;
    const out = new Map<string, NodePosition>();
    for (const ids of columnNodeIds) {
      let hiddenAbove = 0;
      for (const id of ids) {
        const base = id ? layout.positions.get(id) : undefined;
        if (!base) continue;
        if (visibleSet && !visibleSet.has(id)) {
          out.set(id, base);
          hiddenAbove++;
        } else if (hiddenAbove === 0) {
          out.set(id, base);
        } else {
          out.set(id, {
            ...base,
            y: base.y - hiddenAbove * ROW_PITCH * collapseAmount,
          });
        }
      }
    }
    return out;
  }, [layout, columnNodeIds, visibleSet, collapseAmount]);

  useEffect(() => {
    const target = collapsing ? 1 : 0;
    const from = collapseAmountRef.current;
    if (from === target) return;
    // The focused card is the only visible one in its column, so it collapses up
    // by its full row offset. Scroll by that same delta each frame to keep it
    // pinned in the viewport while everything else gathers around it — otherwise
    // selecting a card far down the scroll would collapse it out of view.
    const scroll = scrollRef.current;
    const focusRow = selectedId
      ? layout.positions.get(selectedId)?.row
      : undefined;
    let raf = 0;
    let startMs = 0;
    let pinTop = 0;
    const tick = (now: number) => {
      if (!startMs) {
        startMs = now;
        pinTop = scroll?.scrollTop ?? 0;
      }
      const t = Math.min(1, (now - startMs) / 220);
      const value = from + (target - from) * (1 - (1 - t) ** 3);
      collapseAmountRef.current = value;
      setCollapseAmount(value);
      if (scroll && focusRow !== undefined)
        scroll.scrollTop = pinTop - focusRow * ROW_PITCH * (value - from);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [collapsing, selectedId, layout]);

  const selectedNode = selectedId
    ? (model.nodeById.get(selectedId) ?? null)
    : null;

  // The focused node's effective reach, counted from its connections (direct,
  // inherited, and derived all land as edges), for the bottom toolbar.
  const summary = useMemo(() => {
    if (!selectedId || !connectedIds) return [];
    const counts: Record<NodeKind, number> = {
      user: 0,
      group: 0,
      app: 0,
      skill: 0,
      space: 0,
    };
    for (const id of connectedIds) {
      if (id === selectedId) continue;
      const kind = model.nodeById.get(id)?.kind;
      if (kind) counts[kind]++;
    }
    const NOUN: Record<NodeKind, [string, string]> = {
      user: ["member", "members"],
      group: ["group", "groups"],
      app: ["connector", "connectors"],
      skill: ["skill", "skills"],
      space: ["space", "spaces"],
    };
    const order: NodeKind[] = ["user", "group", "app", "skill", "space"];
    return order
      .filter((k) => counts[k] > 0)
      .map((k) => `${counts[k]} ${counts[k] === 1 ? NOUN[k][0] : NOUN[k][1]}`);
  }, [selectedId, connectedIds, model]);

  // Esc deselects, arrows move across the map, Space opens the highlighted
  // node. Ignored while dragging, typing, or when a modal is open.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (drag) return;
      const el = document.activeElement as HTMLElement | null;
      if (
        el &&
        (el.tagName === "INPUT" ||
          el.tagName === "TEXTAREA" ||
          el.isContentEditable ||
          el.closest("dialog"))
      )
        return;

      if (e.key === "Escape") {
        if (selectedId) {
          e.preventDefault();
          onFocus(null);
        }
        return;
      }
      if (e.key === " " || e.code === "Space") {
        const node = selectedId ? model.nodeById.get(selectedId) : null;
        if (node) {
          e.preventDefault();
          onSelect(node);
        }
        return;
      }
      if (e.key === "h" || e.key === "H") {
        e.preventDefault();
        onToggleHide();
        return;
      }
      if (e.key === "i" || e.key === "I") {
        e.preventDefault();
        onToggleInherited();
        return;
      }

      const dir =
        e.key === "ArrowUp"
          ? "up"
          : e.key === "ArrowDown"
            ? "down"
            : e.key === "ArrowLeft"
              ? "left"
              : e.key === "ArrowRight"
                ? "right"
                : null;
      if (!dir) return;
      e.preventDefault();

      const pos = selectedId ? layout.positions.get(selectedId) : undefined;
      if (!pos) {
        const first = columnNodeIds.find((c) => c.length > 0)?.[0];
        if (first) onFocus(first);
        return;
      }
      let target: string | undefined;
      if (dir === "up") target = columnNodeIds[pos.col]?.[pos.row - 1];
      else if (dir === "down") target = columnNodeIds[pos.col]?.[pos.row + 1];
      else {
        const adj = columnNodeIds[dir === "left" ? pos.col - 1 : pos.col + 1];
        if (adj?.length) target = adj[Math.min(pos.row, adj.length - 1)];
      }
      if (target) onFocus(target);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    selectedId,
    model,
    onSelect,
    onFocus,
    onToggleHide,
    onToggleInherited,
    layout,
    columnNodeIds,
    drag,
  ]);

  // Keep the highlighted node in view when navigating by keyboard.
  useEffect(() => {
    if (!selectedId) return;
    scrollRef.current
      ?.querySelector(`[data-node-id="${CSS.escape(selectedId)}"]`)
      ?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [selectedId]);

  // Typing a search drops any selection (so the dimming follows the query, not
  // a path) and pulls the first match into view.
  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (!q) return;
    onFocus(null);
    const first = model.nodes.find((n) => nodeMatches(n, q));
    if (first)
      scrollRef.current
        ?.querySelector(`[data-node-id="${CSS.escape(first.id)}"]`)
        ?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [query, model, onFocus]);

  // A press becomes a drag once the pointer moves past the threshold; a press
  // without movement is a click (highlight). Window listeners live only for the
  // duration of the interaction.
  const beginInteraction = useCallback(
    (node: GraphNode, e: ReactPointerEvent) => {
      if (e.button !== 0) return;
      // Cards preventDefault their pointerdown, which suppresses the mousedown
      // the menu's outside-click dismissal relies on; close it here instead.
      setMenu(null);
      e.preventDefault();
      const startX = e.clientX;
      const startY = e.clientY;
      const rect = e.currentTarget.getBoundingClientRect();
      const grabDX = startX - rect.left;
      const grabDY = startY - rect.top;
      let dragging = false;

      // Only a source that can be granted somewhere is draggable; a press on a
      // node with no permitted target just selects it.
      const canDragSource = model.nodes.some(
        (n) =>
          n.id !== node.id &&
          canDrop(node.kind, n.kind) &&
          !(membershipLocked && isMembershipDrop(node, n)) &&
          canGrant(node, n)
      );

      const hit = (cx: number, cy: number): string | null => {
        const id = document
          .elementFromPoint(cx, cy)
          ?.closest("[data-node-id]")
          ?.getAttribute("data-node-id");
        if (!id || id === node.id) return null;
        const t = model.nodeById.get(id);
        if (!t || !canDrop(node.kind, t.kind)) return null;
        if (membershipLocked && isMembershipDrop(node, t)) return null;
        if (!canGrant(node, t)) return null;
        return id;
      };

      let lastX = startX;
      let lastY = startY;
      let scrollRaf = 0;

      const update = () =>
        setDrag({
          source: node,
          x: lastX,
          y: lastY,
          dx: grabDX,
          dy: grabDY,
          overId: hit(lastX, lastY),
        });

      // Holding near an edge pans the graph (continuously, even with the pointer
      // still) so cards outside the viewport can be reached and dropped onto.
      const edgeScroll = () => {
        scrollRaf = 0;
        const scroll = scrollRef.current;
        if (!dragging || !scroll) return;
        const r = scroll.getBoundingClientRect();
        const speed = (depth: number) =>
          Math.min(EDGE_SCROLL_MAX, Math.max(0, depth) * 0.3);
        const dx =
          lastX < r.left + EDGE_SCROLL_ZONE
            ? -speed(r.left + EDGE_SCROLL_ZONE - lastX)
            : lastX > r.right - EDGE_SCROLL_ZONE
              ? speed(lastX - (r.right - EDGE_SCROLL_ZONE))
              : 0;
        const dy =
          lastY < r.top + EDGE_SCROLL_ZONE
            ? -speed(r.top + EDGE_SCROLL_ZONE - lastY)
            : lastY > r.bottom - EDGE_SCROLL_ZONE
              ? speed(lastY - (r.bottom - EDGE_SCROLL_ZONE))
              : 0;
        if (!dx && !dy) return;
        scroll.scrollLeft += dx;
        scroll.scrollTop += dy;
        update();
        scrollRaf = requestAnimationFrame(edgeScroll);
      };

      const onMove = (ev: PointerEvent) => {
        if (!canDragSource) return;
        if (!dragging) {
          if (
            Math.hypot(ev.clientX - startX, ev.clientY - startY) <=
            DRAG_THRESHOLD
          )
            return;
          dragging = true;
          playClick();
          document.body.setAttribute("data-ag-dragging", "true");
        }
        lastX = ev.clientX;
        lastY = ev.clientY;
        update();
        if (!scrollRaf) scrollRaf = requestAnimationFrame(edgeScroll);
      };

      const onUp = (ev: PointerEvent) => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        if (scrollRaf) cancelAnimationFrame(scrollRaf);
        document.body.removeAttribute("data-ag-dragging");
        if (dragging) {
          const overId = hit(ev.clientX, ev.clientY);
          setDrag(null);
          const target = overId ? model.nodeById.get(overId) : null;
          if (target) {
            playClick();
            onDrop(node, target);
          }
        } else {
          playClick();
          onFocus(node.id);
        }
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [model, onDrop, onFocus, membershipLocked, canGrant]
  );

  const handleMore = useCallback(
    (n: GraphNode) => setMenu({ targetId: n.id }),
    []
  );

  const toggleSort = (kind: NodeKind) =>
    setColumnSort((prev) => {
      const cur = prev[kind];
      const next =
        cur === undefined ? "asc" : cur === "asc" ? "desc" : undefined;
      return { ...prev, [kind]: next };
    });

  const clearOnBackground = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    if (e.target === e.currentTarget) onFocus(null);
  };

  const nodeState = (node: GraphNode): ChipState => {
    if (drag) {
      if (node.id === drag.source.id) return "dragging";
      if (
        canDrop(drag.source.kind, node.kind) &&
        !(membershipLocked && isMembershipDrop(drag.source, node)) &&
        canGrant(drag.source, node)
      )
        return node.id === drag.overId ? "dropTarget" : "droppable";
      return "normal";
    }
    if (selectedId) {
      if (node.id === selectedId) return "active";
      if (connectedIds?.has(node.id)) return "normal";
      return hideUnrelated ? "hidden" : "dim";
    }
    if (matchedIds)
      return matchedIds.has(node.id)
        ? "normal"
        : hideUnrelated
          ? "hidden"
          : "dim";

    const layers = nodeLayers.get(node.id);
    const layerDimmed =
      layers !== undefined &&
      !(
        (layers.has("direct") && showDirect) ||
        (layers.has("inherited") && showInherited) ||
        (layers.has("derived") && showAvailable)
      );
    return layerDimmed ? "dim" : "normal";
  };

  const menuTarget = menu ? (model.nodeById.get(menu.targetId) ?? null) : null;
  let menuRemove: (() => void) | undefined;
  let menuRemoveLabel = "Revoke access";
  let menuRemoveReason: string | undefined;
  if (menu && menuTarget) {
    if (!selectedId) {
      menuRemoveReason = "Select a node first to choose what to revoke.";
    } else if (selectedId === menu.targetId) {
      menuRemoveReason = "Select another node it connects to.";
    } else {
      const active = model.nodeById.get(selectedId);
      const between = model.edges.filter(
        (e) =>
          (e.sourceId === selectedId && e.targetId === menu.targetId) ||
          (e.sourceId === menu.targetId && e.targetId === selectedId)
      );
      const membership = between.some((e) => e.type === "membership");
      if (active && membership && membershipLocked) {
        const group = active.kind === "group" ? active : menuTarget;
        menuRemoveLabel = `Remove from ${group.name}`;
        menuRemoveReason = membershipReason ?? undefined;
      } else if (
        active &&
        (membership || between.some((e) => e.type === "direct"))
      ) {
        if (canGrant(active, menuTarget)) {
          menuRemove = () => onRemove(active, menuTarget);
        } else {
          menuRemoveReason = "You don’t have permission to change this access.";
        }
        if (membership) {
          const group = active.kind === "group" ? active : menuTarget;
          menuRemoveLabel = `Remove from ${group.name}`;
        }
      } else if (between.some((e) => e.type === "inherited")) {
        menuRemoveReason =
          "Inherited through a group — remove it from the group instead.";
      } else if (between.some((e) => e.type === "derived")) {
        menuRemoveReason =
          "Available through the space’s members — there’s no direct grant to remove.";
      } else {
        menuRemoveReason = "No direct access between these to remove.";
      }
    }
  }

  return (
    <Viewport>
      <Scroll ref={scrollRef} data-ag-scroll onMouseDown={clearOnBackground}>
        <HeaderBar style={{ width: layout.width + 48 }}>
          {layout.columns.map((c) => {
            const dir = columnSort[c.kind];
            return (
              <ColumnSlot key={c.kind} style={{ left: 24 + c.x }}>
                <SortableHeaderContent onClick={() => toggleSort(c.kind)}>
                  <Text variant="text-small" color="inherit">
                    {c.label}
                  </Text>
                  <SortArrow data-active={!!dir} data-direction={dir ?? "asc"}>
                    <Icon name="arrow-up" size={12} color="currentColor" />
                  </SortArrow>
                </SortableHeaderContent>
              </ColumnSlot>
            );
          })}
        </HeaderBar>

        <Canvas
          ref={canvasRef}
          style={{ width: layout.width, height: layout.height }}
          onMouseDown={clearOnBackground}
        >
          <GraphEdges
            edges={model.edges}
            positions={renderPositions}
            width={layout.width}
            height={layout.height}
            focusId={drag ? null : selectedId}
            visibleSet={visibleSet}
            hideUnrelated={hideUnrelated}
            showDirect={showDirect}
            showInherited={showInherited}
            showAvailable={showAvailable}
            hoverLayer={hoverLayer}
          />
          <HoverEdges
            edges={model.edges}
            positions={renderPositions}
            width={layout.width}
            height={layout.height}
            canvasRef={canvasRef}
            active={!drag && !selectedId}
            visibleSet={visibleSet}
            hideUnrelated={hideUnrelated}
            showDirect={showDirect}
            showInherited={showInherited}
            showAvailable={showAvailable}
          />
          {model.nodes.map((node) => {
            const pos = renderPositions.get(node.id);
            if (!pos) return null;
            return (
              <GraphNodeChip
                key={node.id}
                node={node}
                position={pos}
                leftVisual={visualById.get(node.id) ?? null}
                state={nodeState(node)}
                onPointerDown={beginInteraction}
                onMore={handleMore}
              />
            );
          })}
        </Canvas>

        {drag &&
          createPortal(
            <Ghost style={{ left: drag.x - drag.dx, top: drag.y - drag.dy }}>
              {visualById.get(drag.source.id)}
              <GhostBody>
                <Text
                  variant="text-small-md"
                  ellipsis
                  color="var(--text-primary)"
                >
                  {drag.source.name}
                </Text>
                {drag.source.subtitle && (
                  <Text
                    variant="text-smaller-md"
                    ellipsis
                    color="var(--text-secondary)"
                  >
                    {drag.source.subtitle}
                  </Text>
                )}
              </GhostBody>
            </Ghost>,
            document.body
          )}

        {menu && menuTarget && (
          <NodeContextMenu
            targetId={menu.targetId}
            onView={() => onSelect(menuTarget)}
            onRemove={menuRemove}
            removeLabel={menuRemoveLabel}
            removeReason={menuRemoveReason}
            actions={nodeActions(menuTarget)}
            onClose={() => setMenu(null)}
          />
        )}
      </Scroll>
      <BottomToolbar
        node={selectedNode}
        visual={selectedNode ? footerVisual(selectedNode) : null}
        summary={summary}
        hideUnrelated={hideUnrelated}
        onToggleHide={onToggleHide}
        showDirect={showDirect}
        onToggleDirect={() => toggleFilter("hideDirect", hideDirect)}
        showInherited={showInherited}
        onToggleInherited={onToggleInherited}
        showAvailable={showAvailable}
        onToggleAvailable={() => toggleFilter("hideAvailable", hideAvailable)}
        onHoverLayer={setHoverLayer}
      />
    </Viewport>
  );
}
